import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from './AuthContext';
import { useAuthToken } from '../utils/authTokenManager';
import axios from 'axios';

const Auth0Context = createContext();

export const useAuth0Context = () => {
  const context = useContext(Auth0Context);
  if (!context) {
    throw new Error('useAuth0Context must be used within an Auth0ContextProvider');
  }
  return context;
};

export const Auth0ContextProvider = ({ children }) => {
  // Get traditional auth context
  const traditionalAuth = useAuth();
  
  // Check if Auth0 is available (will be undefined if Auth0Provider isn't wrapping this)
  let auth0Available = true;
  let auth0Hooks = null;
  try {
    auth0Hooks = useAuth0();
  } catch (e) {
    auth0Available = false;
  }
  
  const {
    user: auth0User = null,
    isAuthenticated: auth0IsAuthenticated = false,
    isLoading: auth0IsLoading = false,
    error: auth0Error = null,
    loginWithRedirect = () => {},
    loginWithPopup = () => {},
    logout: auth0Logout = () => {},
    getAccessTokenSilently = () => Promise.resolve(null),
    getIdTokenClaims = () => Promise.resolve(null)
  } = auth0Hooks || {};

  const [appUser, setAppUser] = useState(null);
  const [userProfiles, setUserProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  
  // If Auth0 is not available, use traditional auth
  useEffect(() => {
    if (!auth0Available && traditionalAuth.user) {
      console.log('📧 Using traditional auth, user:', traditionalAuth.user);
      setAppUser(traditionalAuth.user);
      setShowProfileSelector(false);
    }
  }, [auth0Available, traditionalAuth.user]);

  // Mock user profiles for demonstration
  const mockUserProfiles = [
    {
      id: 'ops_profile',
      name: 'Operations Manager',
      email: 'sarah.johnson@company.com',
      role: 'operations_manager',
      department: 'Operations Management',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      description: 'Monitor plant operations and efficiency metrics'
    },
    {
      id: 'maint_profile',
      name: 'Maintenance Engineer',
      email: 'alex.thompson@company.com',
      role: 'maintenance_engineer',
      department: 'Maintenance Engineering',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      description: 'Handle equipment diagnostics and maintenance tasks'
    },
    {
      id: 'supervisor_profile',
      name: 'Plant Supervisor',
      email: 'david.miller@company.com',
      role: 'plant_supervisor',
      department: 'Plant Operations',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      description: 'Supervise production lines and safety protocols'
    },
    {
      id: 'admin_profile',
      name: 'System Administrator',
      email: 'admin@company.com',
      role: 'admin',
      department: 'IT & Systems',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      description: 'Manage system configuration and user access'
    }
  ];

  // Load user profiles based on Auth0 user
  useEffect(() => {
    if (auth0IsAuthenticated && auth0User) {
      // In a real app, you'd fetch available profiles from your backend
      // based on the authenticated user's permissions
      setUserProfiles(mockUserProfiles);
      
      // Check if user needs to select a profile
      const savedProfile = localStorage.getItem(`selectedProfile_${auth0User.sub}`);
      if (savedProfile) {
        const profile = mockUserProfiles.find(p => p.id === savedProfile);
        if (profile) {
          setSelectedProfile(profile);
          setAppUser(profile);
        }
      } else {
        setShowProfileSelector(true);
      }
    }
  }, [auth0IsAuthenticated, auth0User]);

  // Store auth token for profile operations
  // Use centralized token manager (no hook violations)
  const profileAuthToken = useAuthToken(auth0IsAuthenticated && auth0User ? auth0User : null, getAccessTokenSilently);

  // Wrapper function that uses stored token (no hook violations)
  const handleSelectProfile = async (profile) => {
    console.log('🎯 Selecting profile with stored token:', {
      profileId: profile.id,
      role: profile.role,
      hasToken: !!profileAuthToken
    });

    // Set profile locally first
    setSelectedProfile(profile);
    setAppUser(profile);
    setShowProfileSelector(false);
    if (auth0User) {
      localStorage.setItem(`selectedProfile_${auth0User.sub}`, profile.id);
    }

    // Sync with backend if token available
    if (profileAuthToken) {
      try {
        await selectProfile(profile, profileAuthToken);
      } catch (error) {
        console.error('❌ [Auth0Context] Profile sync failed:', error);
        // Profile is already set locally, so sync failure is not critical
      }
    }
  };

  const selectProfile = async (profile, token = null) => {
    console.log('🎯 Selecting profile:', {
      profileId: profile.id,
      role: profile.role,
      department: profile.department
    });

    setSelectedProfile(profile);
    setAppUser(profile);
    setShowProfileSelector(false);
    
    // Save selection to localStorage
    if (auth0User) {
      localStorage.setItem(`selectedProfile_${auth0User.sub}`, profile.id);
    }

    // Sync with backend - with timeout and error handling
    try {
      console.log('🔄 Starting profile sync...');
      if (!token) {
        console.error('❌ No auth token provided to selectProfile');
        throw new Error('Authentication token required for profile sync');
      }
      
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile sync timeout after 10 seconds')), 10000)
      );
      
      // Race between sync request and timeout
      const syncPromise = axios.post('auth/sync-profile', {
        auth0Id: auth0User.sub,
        profile: profile,
        auth0User: auth0User
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 8000 // 8 second axios timeout
      });

      const response = await Promise.race([syncPromise, timeoutPromise]);
      console.log('✅ Profile sync completed successfully:', {
        userRole: response.data.user?.role,
        userEmail: response.data.user?.email
      });
      
    } catch (error) {
      console.error('❌ Profile sync error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        profileRole: profile.role
      });
      
      // Show user-friendly error message based on error type
      if (error.message === 'Profile sync timeout after 10 seconds') {
        console.warn('⚠️  Profile sync timed out - continuing with local profile');
      } else if (error.response?.status === 401) {
        console.warn('⚠️  Authentication issue during profile sync - user may need to re-login');
      } else if (error.response?.status >= 500) {
        console.warn('⚠️  Server error during profile sync - continuing with local profile');
      } else {
        console.warn('⚠️  Profile sync failed - continuing with local profile');
      }
      
      // Continue anyway - don't block the user experience
      // The app should still work even if backend sync fails
    }
  };

  const switchProfile = () => {
    console.log('🔄 Switching profile - clearing current selection');
    
    // Clear any cached state
    if (auth0User) {
      localStorage.removeItem(`selectedProfile_${auth0User.sub}`);
    }
    
    // Reset state to show profile selector
    setShowProfileSelector(true);
    setSelectedProfile(null);
    setAppUser(null);
    
    // Clear any potential Auth0 error state
    if (window.history.length > 1) {
      // Use replace state to avoid navigation issues
      window.history.replaceState(null, null, window.location.pathname);
    }
  };

  // Add error recovery function
  const handleAuth0Error = () => {
    console.log('🔧 Attempting Auth0 error recovery...');
    
    // Clear any problematic state
    localStorage.removeItem('auth0.is.authenticated');
    sessionStorage.clear();
    
    // Reset profile state
    setShowProfileSelector(false);
    setSelectedProfile(null);
    setAppUser(null);
    
    // Redirect to login
    window.location.href = '/login';
  };

  const loginWithGoogle = () => {
    loginWithRedirect({
      connection: 'google-oauth2',
      screen_hint: 'login'
    });
  };

  const loginWithLinkedIn = () => {
    loginWithRedirect({
      connection: 'linkedin',
      screen_hint: 'login'
    });
  };

  const loginWithEmail = () => {
    loginWithRedirect({
      connection: 'Username-Password-Authentication',
      screen_hint: 'login'
    });
  };

  const logout = (options = {}) => {
    // Clear local storage
    if (auth0User) {
      localStorage.removeItem(`selectedProfile_${auth0User.sub}`);
    }
    setSelectedProfile(null);
    setAppUser(null);
    setShowProfileSelector(false);
    
    // Auth0 logout - redirect to ProCogia Solutions page
    auth0Logout({
      returnTo: "https://procogia.com/solutions/",
      ...options
    });
  };

  const value = {
    // Auth0 primitives
    auth0User,
    auth0IsAuthenticated,
    auth0IsLoading,
    auth0Error,
    getAccessTokenSilently,
    getIdTokenClaims,
    
    // App-specific user management
    appUser: appUser || traditionalAuth.user,
    userProfiles,
    selectedProfile,
    showProfileSelector,
    
    // Actions
    loginWithGoogle,
    loginWithLinkedIn,
    loginWithEmail,
    loginWithPopup,
    selectProfile: handleSelectProfile,
    switchProfile,
    logout: auth0Available ? logout : traditionalAuth.logout,
    
    // Computed values
    isAuthenticated: auth0Available 
      ? (auth0IsAuthenticated && (!!appUser || showProfileSelector))
      : traditionalAuth.isAuthenticated,
    isLoading: auth0Available ? auth0IsLoading : traditionalAuth.loading,
    hasRole: (role) => {
      const user = appUser || traditionalAuth.user;
      return user?.role === role || user?.role === 'admin';
    },
    hasAnyRole: (roles) => {
      const user = appUser || traditionalAuth.user;
      return user?.role === 'admin' || roles.includes(user?.role);
    }
  };

  return (
    <Auth0Context.Provider value={value}>
      {children}
    </Auth0Context.Provider>
  );
}; 